<script setup lang="ts">
import { inject, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

const route = useRoute()
const opsStore = useOpsStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')
const taId = route.params.taId as string

const formKey = computed(() => `SAR110_${taId}`)
const formData = computed(() => formsStore.getFormData(formKey.value))

const op = computed(() => opsStore.selectedOp)

// Live derived values from stores
const teamAssignment = computed(() =>
  assignmentsStore.teamAssignments.find((ta) => ta.ta_id === taId),
)
const team = computed(() => {
  const teamId = teamAssignment.value?.team_id
  return teamId ? teamsStore.teams.find((t) => t.team_id === teamId) : undefined
})
const assignment = computed(() => {
  const assignmentId = teamAssignment.value?.assignment_id
  return assignmentId ? assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId) : undefined
})
const teamMembers = computed(() => {
  const teamId = teamAssignment.value?.team_id
  if (!teamId) return []
  return personnelStore.roster.filter((p) => p.team_id === teamId && !p.time_out)
})
const teamMembersText = computed(() =>
  teamMembers.value.map((p) => `${p.first} ${p.last}`).join(', '),
)

// Pre-populate from debrief_cols on first open
watch(
  teamAssignment,
  async (ta) => {
    if (!ta || !incidentId) return
    const existing = formsStore.getFormData(formKey.value)
    if (existing['_initialized']) return
    const cols = (ta.debrief_cols as Record<string, unknown>) ?? {}
    const writes: Promise<void>[] = [
      formsStore.saveFormField(incidentId, formKey.value, '_initialized', true),
    ]
    const fields: Record<string, keyof typeof cols> = {
      assignment_summary: 'assignment_summary',
      search_efforts: 'search_efforts',
      areas_unsearched: 'areas_unsearched',
      clues_found: 'clues_found',
      hazards_problems: 'hazards_problems',
      suggestions: 'suggestions',
    }
    for (const [formField, colKey] of Object.entries(fields)) {
      if (cols[colKey] && !existing[formField]) {
        writes.push(formsStore.saveFormField(incidentId, formKey.value, formField, cols[colKey] as string))
      }
    }
    await Promise.all(writes)
  },
  { immediate: true },
)

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, formKey.value, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}
</script>

<template>
  <FormShell
    :form-type="formKey"
    title="SAR 110 · Team Debrief"
    back-route="incident-form-sar110-list"
    back-label="Team Debriefs"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 110 · Team Debrief</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">
            {{ team?.team_identifier ?? 'Team Debrief' }}
          </h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Post-assignment debrief for {{ assignment?.assignment_name ?? 'this assignment' }}.</p>
      </div>

      <!-- Team / Assignment (read-only, from stores) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Team / Assignment</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Identifier</label>
            <p class="font-mono text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ team?.team_identifier ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operational Period</label>
            <p class="font-mono text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ op ? `OP ${op.op_number}` : '—' }}</p>
          </div>
          <div class="col-span-2">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Assignment</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ assignment?.assignment_name ?? '—' }}</p>
          </div>
          <div class="col-span-2">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Members</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded min-h-[38px]">{{ teamMembersText || '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Assignment Summary -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Assignment Summary</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('assignment_summary')"
            placeholder="Brief summary of the assignment and overall outcome…"
            :rows="3"
            @blur="save('assignment_summary', $event)"
          />
        </div>
      </div>

      <!-- Search Efforts -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Search Efforts</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('search_efforts')"
            placeholder="Describe the areas searched, methods used, and effort expended…"
            :rows="4"
            @blur="save('search_efforts', $event)"
          />
        </div>
      </div>

      <!-- Areas Unsearched -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Areas Unsearched</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('areas_unsearched')"
            placeholder="Describe any areas within the assignment that were not searched and why…"
            :rows="3"
            @blur="save('areas_unsearched', $event)"
          />
        </div>
      </div>

      <!-- Clues Found -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Clues Found</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('clues_found')"
            placeholder="List any clues or evidence found during the search…"
            :rows="3"
            @blur="save('clues_found', $event)"
          />
        </div>
      </div>

      <!-- Hazards / Problems -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Hazards & Problems</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('hazards_problems')"
            placeholder="Describe any hazards encountered or operational problems…"
            :rows="3"
            @blur="save('hazards_problems', $event)"
          />
        </div>
      </div>

      <!-- Suggestions -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Suggestions</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('suggestions')"
            placeholder="Recommendations for continued search operations…"
            :rows="3"
            @blur="save('suggestions', $event)"
          />
        </div>
      </div>
    </div>
  </FormShell>
</template>
